import { KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import CloseCircle from 'assets/icons/CloseCircle';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';
import Card from 'components/ui/Card';
import COLOR from 'constants/color';
import { EMPTY_STRING, KEY_BOARD } from 'constants/general';
import { ATTRIB } from 'constants/test';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { updateMoreOptionActivityTags } from 'store/reducer/modal/slice';
import { moreOptionModalActivitySelector } from 'store/reducer/modal/more-options.modal.selectors';
import { isTagValid } from 'utils/validators';

const Tags: React.FC = () => {
  const dispatch = useAppDispatch();
  const [tag, setTag] = useState(EMPTY_STRING);
  const activity = useAppSelectorShallow(moreOptionModalActivitySelector);

  const isValid = useMemo(() => isTagValid(tag), [tag]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== KEY_BOARD.ENTER.KEY) {
      return;
    }

    const trimmedTag = tag.trim();
    if (!trimmedTag) {
      return;
    }

    const duplicateTagFound = activity?.tags?.some(
      (existingTag) => existingTag === trimmedTag
    );
    if (duplicateTagFound) {
      toast.error(t('tag_has_already_added', { tag: trimmedTag }));
      return;
    }

    setTag(EMPTY_STRING);
    dispatch(
      updateMoreOptionActivityTags([...(activity?.tags ?? []), trimmedTag])
    );
  };

  const handleRemove = useCallback(
    (tagToRemove: string) => {
      dispatch(
        updateMoreOptionActivityTags(
          (activity?.tags ?? []).filter(
            (existingTag) => existingTag !== tagToRemove
          )
        )
      );
    },
    [activity?.tags, dispatch]
  );

  return (
    <Card
      className='flex flex-col ~gap-3/4'
      data-testid={ATTRIB.DATA_TESTID.TAGS_CONTAINER}>
      <h3 className='~text-xs/base font-semibold text-black dark:text-white'>
        {t('tabs_revamp.tags.tags')}
      </h3>
      <FloatingLabelInput
        type='text'
        value={tag}
        onChange={(e) => setTag(e.target.value)}
        onKeyDown={(event) => isValid && handleKeyDown(event)}
        size='small'
        showLabel
        placeholder={t('tabs_revamp.tags.new_tags')}
        data-testid={ATTRIB.DATA_TESTID.TAG_INPUT}
      />
      {activity?.tags && activity.tags.length > 0 && (
        <div className='w-full flex flex-wrap ~gap-2/3 ~pt-1/2'>
          {activity.tags.map((tagLabel, idx) => (
            <div
              key={`${idx}${tagLabel}`}
              className='relative bg-gray-600 dark:bg-secondary-dark-500 rounded ~px-2/2.5 ~py-0.5/1 shadow'>
              <span className='~text-xs/sm font-medium text-white dark:text-secondary-dark-900'>
                {`#${tagLabel}`}
              </span>
              <button
                type='button'
                className='absolute -top-2 -right-2 bg-white dark:bg-secondary-dark-300 rounded-full'
                onClick={() => handleRemove(tagLabel)}
                aria-label={t('tabs_revamp.tags.remove')}>
                <CloseCircle styles='w-4 h-auto' fill={COLOR.SEMANTIC.ERROR} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default Tags;
